import BookingsCountLabel from 'components/Common/Labels/BookingsCountLabel'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import LayoutContainer from 'components/Common/LayoutContainer'
import { ImageParams } from 'components/Common/ResponsiveImage'
import CardCarousel from 'components/Luxkit/Carousel/CardCarousel'
import ImageCarousel from 'components/Luxkit/Carousel/ImageCarousel'
import LineArrowsRightDownIcon from 'components/Luxkit/Icons/line/LineArrowsRightDownIcon'
import LineBedIcon from 'components/Luxkit/Icons/line/LineBedIcon'
import LabelGroup from 'components/Luxkit/Label/LabelGroup'
import StructureLabel from 'components/Luxkit/Label/StructureLabel'
import Heading from 'components/Luxkit/Typography/Heading'
import Group from 'components/utils/Group'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { EmptyArray, groupBy } from 'lib/array/arrayUtils'
import React, { useMemo } from 'react'
import { FlightsInfoByRoom, getAvailablePackagesByRoom, getFlightsInfoByRoom, PackagesOptionsViewsByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import { isAvailableForPurchase } from 'selectors/offerPage/offerPageSelectors'
import styled from 'styled-components'
import MobileMultipleRoomsBookingPackageOption from './MobileMultipleRoomsBookingPackageOption'
import { rem } from 'polished'
import MessageBanner from 'components/Luxkit/Banners/MessageBanner'
import BodyText from 'components/Luxkit/Typography/BodyText'
import TextLink from 'components/Luxkit/TextLink'
import MobileMultipleRoomsBookingPriceDetails from './MobileMultipleRoomsBookingPriceDetails'

const MobileHuggingContainer = styled.div`
  padding: 0;
  display: grid;
  position: relative;
  grid-template-rows: 210px 1fr;
`

const RoomHeading = styled(Group)`
  padding-top: ${rem(20)};
  padding-bottom: ${rem(8)};
`

const Container = styled(LayoutContainer)`
  padding-right: ${rem(20)};
  padding-left: ${rem(20)};
`

const PackageCarousel = styled(CardCarousel)`
  margin: 0 ${rem(20)};
  padding-top: ${rem(8)};
  padding-bottom: ${rem(20)};
`

const NotAvailableContainer = styled(Group)`
  padding: ${rem(12)} 0 ${rem(20)} 0;
`

interface Props {
  availablePackagesByRoom: PackagesOptionsViewsByRoom;
  rooms: Array<App.RoomOccupants>;
  offer: App.HotelOffer;
  checkIn?: string;
  availableForPurchase?: boolean;
  flightsInfoByRoom: FlightsInfoByRoom;
  isFlightsSelected: boolean;
}

const carouselImageParams: ImageParams = { mobileAspectRatio: '16:9', tabletAspectRatio: '16:9', quality: 'good' }

function MobileMultipleRoomsSameRoomContent(
  { availableForPurchase, rooms, availablePackagesByRoom, offer, checkIn, isFlightsSelected, flightsInfoByRoom }: Props,
) {
  const availablePackages = availablePackagesByRoom[rooms[0].roomId]

  const isFlightsForAllRoomsAvailable = useMemo(() => Object.values(flightsInfoByRoom).every(flightsInfo => flightsInfo.isFlightsAvailable), [flightsInfoByRoom])

  const optionsByRoomType = useMemo(() =>
    Array
      .from(groupBy(availablePackages, (pkg) => pkg?.package.roomType?.id).values())
      // Order by availiabilty of rooms in the room type from having the most to the least
      .sort((a, b) => ((b[0].availableRooms ?? 0) - rooms.length) - ((a[0].availableRooms ?? 0) - rooms.length)),
  [availablePackages, rooms.length])

  return (
    <Group direction="vertical" gap={16}>
      {optionsByRoomType.map((roomOption) => {
        if (roomOption.length === 0) {
          return null
        }

        // Same room type for all options, so we pick the first one
        const roomType = roomOption[0].package.roomType
        if (!roomType) {
          return null
        }

        const images = roomType.images ?? EmptyArray as Array<App.Image>
        const isAllRoomsAvailable = (roomOption[0].availableRooms ?? 0) >= rooms.length

        return (
          <Group direction="vertical" key={roomType.id}>
            <MobileHuggingContainer>
              {images.length > 0 &&
                <ImageCarousel
                  imageParams={carouselImageParams}
                  defaultIndex={0}
                  images={images}
                  eagerLoadFirstImage
                  arrowBehaviour="interaction-visible"
                />
              }
            </MobileHuggingContainer>
            <Container>
              <RoomHeading direction="vertical" gap={12}>
                <Group direction="horizontal" gap={8}>
                  <StructureLabel variant="standard">{rooms.length}x</StructureLabel>
                  <Heading variant="heading4">{roomType.name}</Heading>
                </Group>
                <LabelGroup>
                  {!!roomOption[0]?.availableRooms && <RoomsRemainingLabel roomCount={roomOption[0]?.availableRooms} threshold="rate" />}
                  {!!roomType.stats.booked && <BookingsCountLabel bookings={roomType.stats.booked} threshold="room" />}
                </LabelGroup>
                <LabelGroup>
                  {!!roomType.sizeSqm && <StructureLabel variant="light" startIcon={<LineArrowsRightDownIcon />}>{roomType.sizeSqm} m&sup2;</StructureLabel>}
                  {!!roomType.numberOfBedrooms && <StructureLabel variant="light" startIcon={<LineBedIcon />}>{roomType.numberOfBedrooms} bedrooms</StructureLabel>}
                </LabelGroup>
              </RoomHeading>
              { // TODO: hook up functionality to switch to individual room selection
                !isAllRoomsAvailable &&
                  <NotAvailableContainer direction="vertical" gap={12}>
                    <MobileMultipleRoomsBookingPriceDetails
                      room={rooms[0]}
                      packageOption={roomOption[0]}
                      // eslint-disable-next-line react/jsx-no-useless-fragment
                      footerNode={<></>}
                    />
                    <MessageBanner
                      kind="informational-primary"
                      description={
                        <BodyText variant="medium">
                          This room type works for some rooms in your group, but not all. To select it, try <TextLink>choosing rooms individually</TextLink>.
                        </BodyText>}
                    >
                    </MessageBanner>
                  </NotAvailableContainer>
              }
            </Container>

            {isAllRoomsAvailable &&
              <PackageCarousel snap="start" width={roomOption.length === 1 ? '100%' : 'auto'} gap={12}>
                {roomOption.map(packageView => <MobileMultipleRoomsBookingPackageOption
                  key={packageView.package.uniqueKey}
                  packageView={packageView}
                  rooms={rooms}
                  offer={offer}
                  checkIn={checkIn}
                  availableForPurchase={availableForPurchase}
                  isFlightsSelected={isFlightsSelected}
                  isFlightsForAllRoomsAvailable={isFlightsForAllRoomsAvailable}
                />)}
              </PackageCarousel>}
          </Group>
        )
      })}
    </Group>
  )
}

type MappedProps = Pick<Props,
'availablePackagesByRoom'
| 'offer'
| 'rooms'
| 'checkIn'
| 'availableForPurchase'
| 'isFlightsSelected'
| 'flightsInfoByRoom'>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  availablePackagesByRoom: getAvailablePackagesByRoom(state, appState),
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray as Array<App.RoomOccupants>,
  checkIn: state.checkInDate,
  availableForPurchase: isAvailableForPurchase(state, appState),
  isFlightsSelected: state.flights,
  flightsInfoByRoom: getFlightsInfoByRoom(state, appState),
}), MobileMultipleRoomsSameRoomContent)
